import { type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { setLocale } from "../../app/i18n.js";
import { BrandMark, buttonClass, focusRing } from "../../ui/index.js";
import { type SiteLang, siteLangs, siteText } from "./content/index.js";
import { SELLER } from "./seller.js";

/** Код интерфейса для языка сайта: сербский сайт включает сербский интерфейс, остальные английский. */
const uiLocale = (l: SiteLang): string => (l === "sr" ? "sr-Latn" : l);

/** Сайт показываем на сербском или английском; русский интерфейс видит английскую версию. */
export function useSiteLang(): SiteLang {
  const { i18n } = useTranslation();
  const found = siteLangs.find((l) => i18n.language === uiLocale(l));
  return found ?? "en";
}

/** Переключатель языка сайта: список языков из `siteLangs`, новый язык появляется сам. */
function LangSwitch() {
  const lang = useSiteLang();
  return (
    <div className="inline-flex w-fit gap-0.5 rounded-full bg-slate-100 p-0.5 text-xs font-semibold">
      {siteLangs.map((l) => (
        <button
          key={l}
          type="button"
          aria-pressed={lang === l}
          onClick={() => setLocale(uiLocale(l) as "sr-Latn" | "en")}
          className={`min-h-8 rounded-full px-3 uppercase transition ${focusRing} ${lang === l ? "bg-white text-ink shadow-card" : "text-ink-2 hover:text-ink"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

/** Шапка и подвал сайта; ссылки на Terms, Refund и Privacy всегда видны (требование Paddle). */
export function SiteLayout({ children }: { children?: ReactNode }) {
  const lang = useSiteLang();
  const t = siteText[lang];
  const { pathname, hash } = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname нужен, при переходе между страницами прокручиваем наверх.
  useEffect(() => {
    // Якорь (/privacy#data-deletion) после отрисовки; иначе наверх страницы.
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [pathname, hash]);

  const link = ({ isActive }: { isActive: boolean }) =>
    `inline-flex min-h-8 items-center rounded text-sm font-medium whitespace-nowrap transition ${focusRing} ${isActive ? "text-ink" : "text-ink-2 hover:text-ink"}`;
  return (
    <div className="flex min-h-dvh flex-col bg-white">
      <header className="sticky top-0 z-20 border-b border-line bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-[1120px] items-center justify-between gap-2 px-4 py-3 sm:gap-3 lg:px-8">
          {/* На телефоне в шапке только знак: имя продукта и «Цены» есть в hero и подвале, а строка не должна ломаться. */}
          <Link to="/" className={`flex min-w-0 items-center gap-2.5 rounded font-semibold text-ink ${focusRing}`} aria-label={SELLER.productName}>
            <BrandMark className="h-8 w-8 shrink-0" />
            <span className="hidden truncate sm:inline">{SELLER.productName}</span>
          </Link>
          <nav className="flex shrink-0 items-center gap-2 sm:gap-5">
            <NavLink to="/pricing" className={({ isActive }) => `hidden sm:inline-flex ${link({ isActive })}`}>
              {t.nav.pricing}
            </NavLink>
            <Link to="/login" className={buttonClass("primary", "whitespace-nowrap", "sm")}>
              {t.nav.login}
            </Link>
            <LangSwitch />
          </nav>
        </div>
      </header>
      <main className="flex-1">{children ?? <Outlet />}</main>
      <footer className="border-t border-line bg-page">
        <div className="mx-auto grid max-w-[1120px] gap-8 px-4 py-10 text-sm text-ink-2 sm:grid-cols-[1.4fr_1fr_1fr] lg:px-8">
          <div>
            <div className="flex items-center gap-2.5 font-semibold text-ink">
              <BrandMark className="h-7 w-7" />
              {SELLER.productName}
            </div>
            <p className="mt-3 max-w-xs">{t.footer.tagline}</p>
          </div>
          <div>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-3">{t.footer.legal}</div>
            <nav className="flex flex-col gap-1">
              <NavLink to="/pricing" className={link}>
                {t.nav.pricing}
              </NavLink>
              <NavLink to="/terms" className={link}>
                {t.nav.terms}
              </NavLink>
              <NavLink to="/refund" className={link}>
                {t.nav.refund}
              </NavLink>
              <NavLink to="/privacy" className={link}>
                {t.nav.privacy}
              </NavLink>
            </nav>
          </div>
          <div>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-3">{t.footer.contact}</div>
            <p className="break-words">{SELLER.email}</p>
            <div className="mt-4 mb-2 text-xs font-semibold uppercase tracking-wider text-ink-3">{t.footer.language}</div>
            <LangSwitch />
          </div>
        </div>
        <div className="border-t border-line">
          <p className="mx-auto max-w-[1120px] px-4 py-4 text-xs text-ink-3 lg:px-8">
            © 2026 {SELLER.legalName}. {t.footer.rights}
          </p>
        </div>
      </footer>
    </div>
  );
}
